<template>
  <div>
    <label :for="$props.id" class="block text-sm/6 font-medium text-gray-900" v-show="label">{{ label }}</label>
    <div class="mt-2 grid grid-cols-1 relative">
      <input
        ref="inputRef"
        :id="$props.id"
        :name="$props.id"
        :type="inputType"
        :placeholder="$props.placeholder"
        v-bind="$attrs"
        :maxlength="maxlength"
        v-model="value"
        class="block w-full rounded-md px-3 py-3 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 sm:text-sm/6"
        :class="[
          customClass,
          rightPaddingClass,
          {
            'text-red-900 outline-red-300 placeholder:text-red-300 focus:outline-red-600': errorMessage
          }
        ]"
        :aria-invalid="!errorMessage"
        aria-describedby="email-error"
        :aria-labelledby="label ? `${id}-label` : undefined"
        :aria-label="!label ? ariaLabel || placeholder : undefined"
        @blur="blur"
        @click="checkCapsLockStatus"
        @keydown="checkCapsLockStatus"
        @input="update($event.target.value)"
      />

      <!-- Right side icons -->
      <div class="absolute inset-y-0 right-0 flex items-center pr-3 gap-2">
        <slot name="right-icons">
          <!-- Search icon -->
          <MagnifyingGlassIcon v-if="actions.search && !actions.loading" :class="iconClass" />

          <!-- Clear button -->
          <button
            v-if="actions.clear && value && !actions.loading"
            type="button"
            class="flex items-center text-gray-400 hover:text-gray-600 transition"
            @click="clearInput"
          >
            <XMarkIcon :class="iconClass" />
          </button>

          <!-- Loading -->
          <div v-if="actions.loading && !errorMessage" class="flex items-center">
            <div class="w-5 h-5 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin"></div>
          </div>

          <!-- Password toggle -->
          <button
            v-if="actions.password && !actions.loading"
            type="button"
            class="flex items-center text-gray-400 hover:text-indigo-600"
            @click="changePasswordVisibility"
          >
            <EyeIcon v-if="!showPassword" :class="iconClass" />
            <EyeSlashIcon v-else :class="iconClass" />
          </button>

          <!-- Error -->
          <ExclamationCircleIcon
            v-if="errorMessage"
            class="size-5 text-red-500 pointer-events-none"
            aria-hidden="true"
          />
        </slot>
      </div>
      <div
        v-if="append && !actions.search && !actions.clear && !actions.password"
        class="absolute inset-y-0 right-0 flex items-center pr-3"
      >
        <span class="text-sm text-gray-500 bg-gray-100 px-2 py-1 rounded">
          {{ append }}
        </span>
      </div>
    </div>

    <!-- Error Message  dynamic  -->
    <div
      v-show="errorMessage"
      class="mt-1 text-xs text-red-600 h-4 md:h-5 overflow-hidden transition-opacity duration-200"
      :class="errorMessage ? 'opacity-100' : 'opacity-0'"
    >
      {{ errorMessage }}
    </div>
  </div>
</template>

<script>
import { ExclamationCircleIcon } from '@heroicons/vue/20/solid';
import { EyeIcon, EyeSlashIcon, XMarkIcon, MagnifyingGlassIcon } from '@heroicons/vue/24/outline';
import { useField } from 'vee-validate';
import moment from 'moment';
import { ref, onBeforeMount, watch, computed, nextTick } from 'vue';
import { generateYupSchema } from '@/composables/globalHelperFunctions';
import { generateYupSchemaForBsn } from '@/components/shared/validationRules/bsnValidationRules';
import { generateYupSchemaForEmail } from '@/components/shared/validationRules/emailValidationRules';
import { generateYupSchemaForDate } from '@/components/shared/validationRules/dateValidationRules';
import { Number } from 'core-js';

export default {
  name: 'InputGroups',
  inheritedAttrs: true,
  components: {
    ExclamationCircleIcon,
    EyeIcon,
    EyeSlashIcon,
    MagnifyingGlassIcon,
    XMarkIcon
  },
  props: {
    id: {
      default: '',
      type: String
    },
    placeholder: {
      default: '',
      type: String
    },
    type: {
      defualt: 'text',
      type: String
    },
    label: {
      default: '',
      type: String
    },
    modelValue: { type: [String, Number], default: '' },
    maxlength: {
      type: [String, Number]
    },
    rules: {
      default: () => ({}),
      type: Object
    },
    customClass: {
      default: '',
      type: String
    },
    append: {
      type: String,
      default: ''
    },
    actions: {
      type: Object,
      default: () => ({})
    }
  },
  emits: ['update:modelValue', 'isValid', 'clear'],
  setup(props, { emit }) {
    const inputRef = ref(null);
    const schema = ref(null);
    const { value, errorMessage, validate } = useField(props.id, schema);
    const inputType = ref(props.type);
    const isCapsLockVisible = ref(false);
    const showPassword = ref(false);
    const iconClass = 'size-5 text-gray-400 cursor-pointer hover:text-gray-600';

    const actions = computed(() => ({
      search: false,
      clear: false,
      loading: false,
      password: false,
      ...props.actions
    }));

    const rightPaddingClass = computed(() => {
      const count = [
        actions.value.search,
        actions.value.clear,
        actions.value.loading,
        actions.value.password,
        !!errorMessage.value
      ].filter(Boolean).length;

      if (count === 0) return '';
      if (count === 1) return 'pr-10';
      if (count === 2) return 'pr-12';
      return 'pr-14';
    });

    watch(
      () => props.rules,
      () => {
        schema.value = buildSchema();
      },
      { deep: true }
    );

    watch(
      () => props.modelValue,
      (newValue) => {
        value.value = newValue;
      }
    );

    const buildSchema = () => {
      if (props.rules?.isBsn) return generateYupSchemaForBsn(props);
      if (props.rules?.isEmail) return generateYupSchemaForEmail(props);
      if (props.rules?.isDate) return generateYupSchemaForDate(props);

      return generateYupSchema(props);
    };

    const blur = async () => {
      isCapsLockVisible.value = false;
      await runValidation();
    };

    const runValidation = async () => {
      const result = await validate({ mode: 'validated-only' });
      emit('isValid', result.valid);
    };

    const checkCapsLockStatus = (event) => {
      isCapsLockVisible.value =
        event.target.value.toUpperCase() === event.target.value &&
        event.target.value.toLowerCase() !== event.target.value;
    };

    const update = async (newValue) => {
      if (props.rules?.isDate) {
        newValue = newValue.replace(/[^\d./-]/g, '');
        if (/^\d{4}-\d{2}-\d{2}$/.test(newValue)) {
          const [year, month, day] = newValue.split('-');
          newValue = `${day}-${month}-${year}`;
        }
        newValue = newValue.replace(/[./]/g, '-');
        newValue = newValue.replace(/-+/g, '-');
        if (/^\d{8}$/.test(newValue)) {
          newValue = newValue.replace(/^(\d{2})(\d{2})(\d{4})$/, '$1-$2-$3');
        }
        if (newValue.length > 10) {
          newValue = newValue.slice(0, 10);
        }
        let formatted = newValue;
        if (newValue.length === 10) {
          const validDate = moment(newValue, 'DD-MM-YYYY', true);

          if (validDate.isValid()) {
            formatted = validDate.format('DD-MM-YYYY');
          }
        }
        value.value = formatted;
        emit('update:modelValue', formatted);
      } else {
        value.value = newValue;
        emit('update:modelValue', newValue);
      }
      await runValidation();
    };

    const changePasswordVisibility = () => {
      showPassword.value = !showPassword.value;

      inputType.value = inputType.value === 'password' ? 'text' : 'password';
    };

    const clearInput = async () => {
      value.value = '';
      emit('update:modelValue', '');
      emit('clear');
      await validate();
      emit('isValid', false);
      nextTick(() => {
        inputRef.value?.focus();
      });
    };

    onBeforeMount(() => {
      schema.value = buildSchema();
      inputType.value = props.type;
      if (props.modelValue) {
        value.value = props.modelValue;
      }
    });

    return {
      value,
      inputType,
      isCapsLockVisible,
      errorMessage,
      showPassword,
      rightPaddingClass,
      iconClass,
      inputRef,
      blur,
      checkCapsLockStatus,
      update,
      validate,
      changePasswordVisibility,
      clearInput
    };
  }
};
</script>
